import { useState, useEffect, Fragment } from 'react';
import {
  Box, Stack, Typography, Button, InputLabel, Input, Dialog, DialogContent
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTheme } from '@emotion/react';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { collectiveApiRequest } from '../../services';
import { remainingCredits } from '../../store/reducers/auth';
import { ErrorText } from '../errorText';
import { Cloud } from '../../assets';
import { CloudRight } from '../../assets';
import { totalCreditsAndRemaining } from '../../services/creditsService';
import PdfViewer from '../../utils/pdfViewer/PdfViewer';
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";

export function UploadImage({
  setImage,
  setJsonData,
  setloader,
  apiResponse,
  image,
  upload,
  apiUrl,
  setDownloadedUrl,
  errorObject,
  setErrorObject,
  file,
  setFile,
  setStatus,
  setCurlBody,
  setCurlUrl,
  setCurlMethod,
  onFileChange,
  text,
  buttonText = "Try API",
}) {
  const theme = useTheme();
  const form = useForm({ mode: "all" });
  const { register, handleSubmit } = form;
  const [previewImage, setPreviewImage] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [fileError, setFileError] = useState("");
  const [isPdf, setIsPdf] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const dispatch = useDispatch();

  const handleFileChange = (e, value) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const isMp4 = file.type === "video/mp4";
    setFileError("");

    if (isVideo && !isMp4) {
      setFileError("Rejected - Invalid file format");
      setFile((prev) => ({ ...prev, [value]: file }));
      return;
    }

    if (isVideo && file.size > 25 * 1024 * 1024) {
      setFileError("Rejected - File size should be less than 25 mb");
      setFile((prev) => ({ ...prev, [value]: file }));
      return;
    }

    if (isVideo) {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;

        if (duration < 6 || duration > 30) {
          setFileError(
            "Rejected - Video duration should be in 6 sec to 30 secs"
          );
          setFile((prev) => ({ ...prev, [value]: file }));
        } else {
          setFileError("");
          setFile((prev) => ({ ...prev, [value]: file }));
        }
      };

      video.onerror = () => {
        setFileError(
          "Error loading video metadata. Please try another video file."
        );
        setFile((prev) => ({ ...prev, [value]: null }));
        window.URL.revokeObjectURL(video.src);
      };

      video.src = URL.createObjectURL(file);
    } else {
      setFileError("");
      setFile((prev) => ({ ...prev, [value]: file }));
    }
  };

  useEffect(() => {
    if (file) {
      setPageNumber(1);
      const formData = new FormData();
      formData.append("file", file);
      setImage((prev) => ({ ...prev, uploaded: formData }));
      setDownloadedUrl("");

      setCurlBody(formData);
      setCurlUrl(apiUrl);
      setCurlMethod("POST");
    }
  }, [
    file,
    setImage,
    setDownloadedUrl,
    apiUrl,
    setCurlBody,
    setCurlUrl,
    setCurlMethod,
  ]);

  const handlePreview = (uploadedFile) => {
    const url = URL.createObjectURL(uploadedFile);
    setPreviewImage(url);

    const fileType = uploadedFile?.type?.toLowerCase?.() || "";
    const fileName = uploadedFile?.name?.toLowerCase?.() || "";
    const isPdfFile =
      fileType === "application/pdf" || fileName.endsWith(".pdf");
    const isVideoFile = fileType.startsWith("video/");

    setPageNumber(1);

    if (isPdfFile) {
      setIsPdf(true);
      setIsVideo(false);
    } else if (isVideoFile) {
      setIsVideo(true);
      setIsPdf(false);
    } else {
      setIsPdf(false);
      setIsVideo(false);
    }
    setOpenDialog(true);
  };

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const allowedTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/pdf",
    "image/tiff",
    "image/tif",
    "video/mp4",
    "video/avi",
    "video/quicktime",
    "video/x-matroska",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  const handleApiRequest = async () => {
    setloader(true);
    setErrorObject(null);

    Object.entries(file).forEach(([id, file]) => {
      if (!file) {
        setFileError("No file selected");
        setloader(false);
        return;
      }

      if (!allowedTypes.includes(file?.type)) {
        setFileError(`Invalid File Type.`);
        setloader(false);
        return;
      }
    });

    const formData = new FormData();

    if (file) {
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          formData.append(key, formData[key]);
        }
      });
      if (file && typeof file === "object" && !Array.isArray(file)) {
        Object.entries(file).forEach(([value, fileItem]) => {
          formData.append(`${value}`, fileItem);
        });
      } else if (file) {
        formData.append(`file`, file);
      }
    }

    try {
      const res = await collectiveApiRequest(formData, apiUrl);
      setStatus(res?.status);
      if (res?.status === 200) {
        if (apiResponse === "json") {
          setJsonData(res?.data);
        } else {
          const downloadUrl = res?.data?.downloadUrl;
          const status = res?.data?.status;
          setDownloadedUrl(downloadUrl);
          setImage((prev) => ({ ...prev, masked: downloadUrl, status }));
        }

        setTimeout(async () => {
          const remainingCredit = await totalCreditsAndRemaining();
          if (remainingCredit?.status === 200) {
            dispatch(
              remainingCredits({
                credits: remainingCredit?.data?.credit?.remainingCredit,
              })
            );
            setloader(false);
          }
        }, 2000);
      }
    } catch (error) {
      setloader(false);
      setErrorObject(error?.response?.data);
    }
  };

  const files = (
    <li key={file?.name}>
      {file?.name} -{Math.round(file?.size / 1000)} kB
    </li>
  );

  useEffect(() => {
    setErrorObject(null);
    setDownloadedUrl("");
  }, [apiResponse, setErrorObject]);

  useEffect(() => {
    if (errorObject) {
      if (apiResponse === "json") {
        setJsonData(errorObject); 
      } else {
        setImage(() => ({
          error:
            errorObject || errorObject?.error || errorObject?.response?.data,
        }));
      }
    } else {
      setImage((prev) => ({ ...prev, error: null }));
    }
  }, [errorObject, setJsonData, setImage, apiResponse]);

  const isFileUploaded =
    file === null ? false : Object.keys(file)?.length === upload?.length;

  // Add UploadBox component to encapsulate dropzone logic
  function UploadBox({
    item,
    file,
    setFile,
    setFileError,
    handleFileChange,
    handlePreview,
  }) {
    const theme = useTheme();
    const { getRootProps, getInputProps } = useDropzone({
      noClick: true,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        const file = acceptedFiles[0];
        if (!file) return;

        const fileExt = "." + file.name.split(".").pop().toLowerCase();   
        const supportedTypes = upload?.length
        ? upload
            .flatMap(item =>
              item.supportedFiles
                ? item.supportedFiles.split(",").map(type => type.trim())
                : []
            )
        : [];
        
        // ---------- Extension Validation ----------
        if (supportedTypes.length && !supportedTypes.includes(fileExt)) {
          setFileError(`Invalid file format. Allowed: ${supportedTypes.join(", ")}`);
          setFile((prev) => ({ ...prev, [item.value]: null }));
          return;
        }

        const invalidTypes = ["image/gif"];

        if (invalidTypes.includes(file.type)) {
          setFileError("Rejected - Invalid file format");
          setFile((prev) => ({ ...prev, [item.value]: null }));
          return;
        }
        const isVideo = file.type.startsWith("video/");
        const isMp4 = file.type === "video/mp4";

        setFileError("");
        if (isVideo && !isMp4) {
          setFileError("Rejected - Invalid file format");
          setFile((prev) => ({ ...prev, [item.value]: file }));
          return;
        }
        if (isVideo && file.size > 25 * 1024 * 1024) {
          setFileError("Rejected - File size should be less than 25 mb");
          setFile((prev) => ({ ...prev, [item.value]: file }));
          return;
        }
        if (isVideo) {
          const video = document.createElement("video");
          video.preload = "metadata";

          video.onloadedmetadata = () => {
            window.URL.revokeObjectURL(video.src);
            const duration = video.duration;

            if (duration < 6 || duration > 30) {
              setFileError(
                "Rejected - Video duration should be between 6 and 30 seconds"
              );
              setFile((prev) => ({ ...prev, [item.value]: file }));
            } else {
              setFileError("");
            }

            setFile((prev) => ({ ...prev, [item.value]: file }));
          };

          video.onerror = () => {
            setFileError(
              "Error loading video metadata. Please try another video file."
            );
            window.URL.revokeObjectURL(video.src);
          };

          video.src = URL.createObjectURL(file);
          return;
        }
        setFileError("");
        setFile((prev) => ({ ...prev, [item.value]: file }));
      },
      onDropRejected: (fileRejections) => {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === "file-too-large") {
          setFileError("Rejected - File size should be less than 25 mb");
        } else {
          setFileError("Rejected - Invalid file format");
        }
      },
      accept: {
        "image/*": [".png", ".jpg", ".jpeg"],
        "application/pdf": [".pdf"],
        "video/*": [".mp4", ".mov", ".avi", ".mkv"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      },
      multiple: false,
    });

    return (
      <Fragment key={item.id}>
        <Typography
          variant="body1"
          sx={{ padding: "3px 0", color: theme.palette.grey.darken4 }}
        >
          {item.label}
        </Typography>
        <Box
          sx={{
            borderRadius: "12px",
            border: "2px dashed rgba(101, 117, 139, 0.25)",
            minHeight: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
          {...getRootProps()}
        >
          <InputLabel
            htmlFor={`upload-image-${item.id}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              height: "100%",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(`upload-image-${item.value}`).click();
            }}
          >
            {/* <Box
              
              src={file && file[item?.value] ? CloudRight : Cloud}
            /> */}
            <FileUploadOutlinedIcon sx={{ width: "5rem", height: "5rem" }} />
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.textColor.grey,
                whiteSpace: "break-spaces",
                fontWeight: "400",
                textAlign: "center",
              }}
            >
              {file && file[item.value]
                ? "File uploaded"
                : "Click to upload  or drag and drop"}
            </Typography>
            {file && file[item?.value] && <div>{file[item?.value]?.name}</div>}
          </InputLabel>
          <Input
            id={`upload-image-${item.value}`}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => handleFileChange(e, item.value)}
            {...getInputProps()}
          />
        </Box>
        {file && file[item.value] && (
          <Button
            onClick={() => handlePreview(file[item.value])}
            sx={{
              borderRadius: "10px",
              border: "1px solid #E1E7EF",
              backgroundColor: "#FFF",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            Load Sample Data
          </Button>
        )}
        {item.fieldInstruction && (
          <Typography
            variant="body1"
            sx={{ color: theme.palette.textColor.grey, marginTop: "5px" }}
          >
            {item.fieldInstruction}
          </Typography>
        )}
      </Fragment>
    );
  }

  return (
    <>
      <Stack
        component="form"
        onSubmit={handleSubmit(handleApiRequest)}
        spacing={2}
        sx={{ px: { xs: 2, md: 0 }, py: 2 }}
      >
        {upload?.map((item) => (
          <UploadBox
            key={item.id}
            item={item}
            file={file}
            setFile={setFile}
            setFileError={setFileError}
            handleFileChange={handleFileChange}
            handlePreview={handlePreview}
          />
        ))}

        {fileError && <ErrorText>{fileError}</ErrorText>}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1, md: 2 }}
          sx={{ pt: 2 }}
        >
          <Button
            variant="contained"
            type="submit"
            disabled={!isFileUploaded || !!fileError}
            sx={{
              display: "flex",
              width: "100%",
              borderRadius: "10px",
              backgroundColor: "var(--lightBlue-500, #03A9F4)",
              boxShadow:
                "0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.10)",
              display: upload?.length && text?.length ? "none" : "flex",
            }}
          >
            <PlayArrowOutlinedIcon sx={{ width: "3rem", height: "3rem" }} />
            {buttonText}
          </Button>
        </Stack>
      </Stack>

      <PdfViewer
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        isPdf={isPdf}
        previewImage={previewImage}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        isVideo={isVideo}
        useDialog
        file={file}
      />
    </>
  );
}